import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Logo from '../../components/Logo';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!emailOrPhone.trim()) {
      setError('Email or phone number is required');
      return;
    }

    setIsLoading(true);

    try {
      // Send password reset email via SendGrid
      // window.location.origin already includes the port if present (e.g., http://172.16.20.104:4028)
      const redirectUrl = `${window.location.origin}/login?reset=true`;
      
      // Save the current origin for redirect handling in case Supabase redirects to wrong origin
      if (window.location.origin) {
        sessionStorage.setItem('app_origin', window.location.origin);
      }
      
      console.log('Sending password reset with redirect URL:', redirectUrl);
      
      const { data, error: invokeError } = await supabase.functions.invoke('send-password-reset-email', {
        body: {
          emailOrPhone: emailOrPhone.trim(),
          redirectUrl,
        },
      });

      if (invokeError) throw invokeError;
      if (!data?.ok) {
        throw new Error(data?.error || 'Failed to send password reset email');
      }

      setSuccess(true);
      setEmailOrPhone('');
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            <Logo className="mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Reset Your Password</h1>
            <p className="text-muted-foreground text-sm">
              Enter your email or phone number to receive a password reset link
            </p>
          </div>

          {success ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-md flex items-start gap-3">
                <Icon name="CheckCircle" size={20} color="var(--color-green-600)" className="flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-green-800 font-medium">
                    Password reset email sent!
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Check your email for instructions to reset your password. The link will expire in 1 hour.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/login')}
                variant="default"
                fullWidth
                iconName="ArrowLeft"
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-destructive font-medium">{error}</p>
                  </div>
                </div>
              )}

              <Input
                type="text"
                label="Email or Phone Number"
                placeholder="Enter your email or phone number"
                value={emailOrPhone}
                onChange={(e) => setEmailOrPhone(e.target.value)}
                error={error ? undefined : ''}
                required
                disabled={isLoading}
                className="w-full"
                autoFocus
              />

              <div className="space-y-3">
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  loading={isLoading}
                  iconName="Mail"
                  iconPosition="right"
                >
                  Send Reset Link
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  fullWidth
                  onClick={() => navigate('/login')}
                  iconName="ArrowLeft"
                >
                  Back to Login
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
